import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import { Box, Stack, Typography } from '@mui/material'
import LayoutFieldNode from './LayoutFieldNode'
import {
  CANVAS_DEFAULTS,
  clampFieldRect,
  normalizeName,
  toNumber,
} from './layoutCanvasUtils'
import { inferFieldStorage } from '../../utils/layoutUtils'

export default function LayoutDesignSurface({
  layoutConfig,
  setLayoutConfig,
  selectedField,
  setSelectedField,
  showGrid,
}) {
  const canvasRef = useRef(null)
  const [dragState, setDragState] = useState(null)
  const [resizeState, setResizeState] = useState(null)

  const getCanvasRect = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return { width: 0, height: 0 }
    return {
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    }
  }, [])

  const getSafeGeometry = useCallback(
    (field, updates) =>
      clampFieldRect(field, updates, getCanvasRect(), {
        minWidth: CANVAS_DEFAULTS.minFieldWidth,
        minHeight: CANVAS_DEFAULTS.minFieldHeight,
        defaultWidth: CANVAS_DEFAULTS.defaultFieldWidth,
        defaultHeight: CANVAS_DEFAULTS.defaultFieldHeight,
        gridSize: CANVAS_DEFAULTS.gridSize,
        snapToGrid: showGrid,
      }),
    [getCanvasRect, showGrid]
  )

  const commitFieldUpdate = useCallback(
    (fieldId, updates) => {
      setLayoutConfig((prev) =>
        prev.map((field) => {
          if (field.id !== fieldId) return field
          const normalized = getSafeGeometry(field, updates)
          return { ...field, ...normalized }
        })
      )

      setSelectedField((prev) => {
        if (!prev || prev.id !== fieldId) return prev
        const normalized = getSafeGeometry(prev, updates)
        return { ...prev, ...normalized }
      })
    },
    [getSafeGeometry, setLayoutConfig, setSelectedField]
  )

  const handleRemoveField = useCallback(
    (fieldId) => {
      setLayoutConfig((prev) => prev.filter((field) => field.id !== fieldId))
      setSelectedField((prev) => (prev?.id === fieldId ? null : prev))
    },
    [setLayoutConfig, setSelectedField]
  )

  const createFieldFromDrop = useCallback((draggedField, x, y) => {
    const createdAt = Date.now()
    const baseName = draggedField?.name
      ? normalizeName(draggedField.name)
      : `field_${createdAt}`

    return {
      id: createdAt,
      name: baseName || `field_${createdAt}`,
      label: draggedField?.label || draggedField?.name || 'Campo',
      type: draggedField?.type || 'Texto',
      role: draggedField?.role || 'none',
      storage: inferFieldStorage(draggedField),
      editable: Boolean(draggedField?.editable),
      x,
      y,
      width: CANVAS_DEFAULTS.defaultFieldWidth,
      height: CANVAS_DEFAULTS.defaultFieldHeight,
      fontSize: toNumber(draggedField?.fontSize, 14),
      ...(draggedField?.config && typeof draggedField.config === 'object'
        ? { config: draggedField.config }
        : {}),
    }
  }, [])

  const [, drop] = useDrop(
    () => ({
      accept: 'FIELD',
      drop: (item, monitor) => {
        const canvas = canvasRef.current
        const clientOffset = monitor.getClientOffset()
        if (!canvas || !clientOffset) return

        const canvasRect = canvas.getBoundingClientRect()
        const draft = {
          x: clientOffset.x - canvasRect.left,
          y: clientOffset.y - canvasRect.top,
          width: CANVAS_DEFAULTS.defaultFieldWidth,
          height: CANVAS_DEFAULTS.defaultFieldHeight,
        }

        if (item?.id) {
          const target = layoutConfig.find((field) => field.id === item.id)
          if (!target) return
          commitFieldUpdate(item.id, draft)
          return
        }

        const cleanPoint = getSafeGeometry(
          {
            x: 0,
            y: 0,
            width: CANVAS_DEFAULTS.defaultFieldWidth,
            height: CANVAS_DEFAULTS.defaultFieldHeight,
          },
          draft
        )

        const newField = createFieldFromDrop(
          item?.field || item,
          cleanPoint.x,
          cleanPoint.y
        )
        setLayoutConfig((prev) => [...prev, newField])
        setSelectedField(newField)
      },
    }),
    [
      commitFieldUpdate,
      createFieldFromDrop,
      getSafeGeometry,
      layoutConfig,
      setLayoutConfig,
      setSelectedField,
    ]
  )

  useEffect(() => {
    if (!dragState) return undefined

    const onMouseMove = (event) => {
      const deltaX = event.clientX - dragState.startX
      const deltaY = event.clientY - dragState.startY
      commitFieldUpdate(dragState.id, {
        x: dragState.originX + deltaX,
        y: dragState.originY + deltaY,
      })
    }

    const onMouseUp = () => setDragState(null)

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [commitFieldUpdate, dragState])

  useEffect(() => {
    if (!resizeState) return undefined

    const onMouseMove = (event) => {
      const deltaX = event.clientX - resizeState.startX
      const deltaY = event.clientY - resizeState.startY
      commitFieldUpdate(resizeState.id, {
        width: resizeState.originWidth + deltaX,
        height: resizeState.originHeight + deltaY,
      })
    }

    const onMouseUp = () => setResizeState(null)

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [commitFieldUpdate, resizeState])

  const gridBackground = useMemo(
    () =>
      showGrid
        ? {
            backgroundImage:
              'linear-gradient(to right, rgba(148, 163, 184, 0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(148, 163, 184, 0.2) 1px, transparent 1px)',
            backgroundSize: `${CANVAS_DEFAULTS.gridSize}px ${CANVAS_DEFAULTS.gridSize}px`,
          }
        : undefined,
    [showGrid]
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        minHeight: 0,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={0.75}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ flex: '0 0 auto' }}
      >
        <Typography variant="body2" color="text.secondary">
          Área de diseño con soporte de arrastre, resize y navegación por
          teclado.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {layoutConfig.length} campo(s) en el canvas
        </Typography>
      </Stack>

      <Box
        ref={(node) => {
          canvasRef.current = node
          drop(node)
        }}
        onClick={() => setSelectedField(null)}
        sx={{
          ...gridBackground,
          position: 'relative',
          minHeight: { xs: 420, md: 480, lg: 0 },
          flex: 1,
          height: 'auto',
          width: '100%',
          overflow: 'auto',
          backgroundColor: 'background.default',
          backgroundImage: showGrid
            ? gridBackground?.backgroundImage
            : 'linear-gradient(180deg, rgba(15,23,42,0.01) 0%, rgba(15,23,42,0.03) 100%)',
          border: '1px solid',
          borderColor: selectedField ? 'primary.main' : 'divider',
          borderRadius: 3,
          p: 1.5,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24)',
        }}
      >
        {layoutConfig.map((field) => (
          <LayoutFieldNode
            key={field.id}
            field={field}
            isSelected={selectedField?.id === field.id}
            isDragging={dragState?.id === field.id}
            onSelect={(event) => {
              event.stopPropagation()
              setSelectedField(field)
            }}
            onDragStart={(event) => {
              event.stopPropagation()
              if (event.button !== 0) return

              setSelectedField(field)
              setDragState({
                id: field.id,
                startX: event.clientX,
                startY: event.clientY,
                originX: toNumber(field.x, 0),
                originY: toNumber(field.y, 0),
              })
            }}
            onResizeStart={(event) => {
              event.stopPropagation()
              event.preventDefault()
              setResizeState({
                id: field.id,
                startX: event.clientX,
                startY: event.clientY,
                originWidth: toNumber(
                  field.width,
                  CANVAS_DEFAULTS.defaultFieldWidth
                ),
                originHeight: toNumber(
                  field.height,
                  CANVAS_DEFAULTS.defaultFieldHeight
                ),
              })
            }}
            onRemove={(event) => {
              event.stopPropagation()
              handleRemoveField(field.id)
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
